import { gql, useMutation } from '@apollo/client'
import { globalHistory, useLocation, useNavigate } from '@reach/router'
import cx from 'classnames'
import LocalVideoPreview from 'components/Video/LocalVideoPreview'
import Participant from 'components/Video/Participant'
import rollbar from 'helpers/rollbar'
import { useEffect, useState } from 'react'
import useLocalTracks from 'twilio/components/VideoProvider/useLocalTracks/useLocalTracks'
import useLocalAudioToggle from 'twilio/hooks/useLocalAudioToggle/useLocalAudioToggle'
import useParticipants from 'twilio/hooks/useParticipants/useParticipants'
import { VideoProvider } from '../../../twilio/components/VideoProvider'
import useRoomState from '../../../twilio/hooks/useRoomState/useRoomState'
import useVideoContext from '../../../twilio/hooks/useVideoContext/useVideoContext'
import useConnectionOptions from '../../../twilio/utils/useConnectionOptions/useConnectionOptions'
import BulmaMessage from '../../Bulma/BulmaMessage'
import ChatAvatar from '../../Chat/ChatAvatar'
import useChatSession from '../../Chat/useChatSession'
import { DashboardTitle } from '../DashboardTitleContext'
import PageComponent from '../PageComponent'
import Instructions from './Instructions'
import VideoParticipant from './VideoParticipant'
import WaitingRoom from './WaitingRoom'
import {
  CompleteVideoSupervisionMutation,
  CompleteVideoSupervisionMutationVariables,
} from './__generated__/CompleteVideoSupervisionMutation'
import {
  GetNextVideoSuperVisionMutation,
  GetNextVideoSuperVisionMutation_getNextVideoSupervision,
  GetNextVideoSuperVisionMutation_getNextVideoSupervision_videoSupervision,
} from './__generated__/GetNextVideoSuperVisionMutation'

const PromptBeforeUnload = () => {
  const curLocation = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    return globalHistory.listen(({ action, location }) => {
      if ((action === 'PUSH' && !location.pathname.includes('/dashboard/video')) || action === 'POP') {
        if (!window.confirm('Are you sure you want to exit video screening?')) {
          navigate(`${curLocation.pathname}`)
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

interface RoomProps {
  videoSupervision: GetNextVideoSuperVisionMutation_getNextVideoSupervision_videoSupervision
  onKitComplete: (kitId) => void
}

const Room = ({ videoSupervision, onKitComplete }: RoomProps) => {
  const participants = useParticipants()

  if (participants.length === 0) {
    return (
      <div>
        <p>No other participants - the other person might have disconnected.</p>
      </div>
    )
  }

  return (
    <div>
      <PromptBeforeUnload />

      {participants.map((participant) => {
        return (
          <VideoParticipant videoSupervision={videoSupervision} onComplete={(kitId) => onKitComplete(kitId)}>
            <Participant participant={participant} />
          </VideoParticipant>
        )
      })}
    </div>
  )
}

const getNextVideoSupervisionMutation = gql`
  mutation GetNextVideoSuperVisionMutation {
    getNextVideoSupervision {
      videoSupervision {
        id
        kits {
          id
          sku
        }
        order {
          id
          email
        }
      }
      twilioToken
      chatId
    }
  }
`

export const completeVideoSupervisionMutation = gql`
  mutation CompleteVideoSupervisionMutation($input: CompleteVideoSupervisionInput!) {
    completeVideoSupervision(input: $input) {
      success
      videoSupervision {
        id
        queueName
      }
    }
  }
`

const CompleteCallButtons = ({
  videoSupervisionId,
  allKitsCompleted,
  onExit,
}: {
  videoSupervisionId: string
  allKitsCompleted: boolean
  onExit: () => void
}) => {
  const [completeVideoSupervision, { loading }] = useMutation<
    CompleteVideoSupervisionMutation,
    CompleteVideoSupervisionMutationVariables
  >(completeVideoSupervisionMutation, {
    variables: { input: { id: videoSupervisionId } },
  })

  const completeCallAndExit = async () => {
    if (!allKitsCompleted) {
      const confirm = window.confirm(`Not all kits have been completed, are you sure this call should be completed?`)

      if (!confirm) {
        return
      }
    }

    await completeVideoSupervision()
    onExit()
  }
  return (
    <>
      <div className="level-item">
        <button
          className={cx('button is-outline is-info ml-2', {
            'is-loading': loading,
          })}
          onClick={() => completeCallAndExit()}
        >
          Complete Call & Exit
        </button>
      </div>
    </>
  )
}

const InnerPage = () => {
  const roomState = useRoomState()
  const [mediaError, setMediaError] = useState()
  const [permissionsError, setPermissionsError] = useState<string | undefined>()
  const { getAudioAndVideoTracks, removeLocalVideoTrack, connect, isConnecting, localTracks, room } = useVideoContext()
  const { removeLocalAudioTrack } = useLocalTracks()

  const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle()

  const hasAudioTrack = localTracks.some((track) => track.kind === 'audio')

  const hasMediaDevices = navigator.mediaDevices !== undefined

  const [chatId, setChatId] = useState<GetNextVideoSuperVisionMutation_getNextVideoSupervision['chatId']>()

  const { joinChat, chatSession, supportUserSession, secret, customerUserSession } = useChatSession()

  useEffect(() => {
    if (chatId) {
      try {
        const orderId = videoSupervision.order.id.replace(/^Order/, '').replace(/-/g, '')

        joinChat({
          sessionId: orderId,
          sessionToken: orderId,
          type: 'support',
          chatId,
        })
      } catch (e) {
        rollbar.error('VideoSupervisionDashboard - chat engine failed to initialise', e)
        // prevent chatengine issues from impacting video supervision
      }
    }
  }, [chatId])

  const [getNextVideoSupervision, { data }] = useMutation<GetNextVideoSuperVisionMutation>(
    getNextVideoSupervisionMutation
  )

  const mediaConnect = () => {
    setPermissionsError(undefined)

    setMediaError(undefined)
    getAudioAndVideoTracks().catch((error) => {
      rollbar.error('Error acquiring local media:', error)
      console.log('Error acquiring local media:')
      console.dir(error)
      setMediaError(error)
    })
  }

  useEffect(() => {
    if (!mediaError) {
      mediaConnect()
    }
  }, [getAudioAndVideoTracks, mediaError])

  useEffect(() => {
    return () => {
      removeLocalVideoTrack()
      removeLocalAudioTrack()
    }
  }, [removeLocalAudioTrack, removeLocalVideoTrack])

  const [completedKitIds, setCompletedKitIds] = useState<string[]>([])

  const onEnterCall = async () => {
    const resp = await getNextVideoSupervision()

    await connect(resp.data.getNextVideoSupervision.twilioToken)
    setChatId(resp.data.getNextVideoSupervision.chatId)
  }

  const videoSupervision = data?.getNextVideoSupervision?.videoSupervision

  return (
    <>
      {(mediaError || permissionsError) && (
        <>
          <BulmaMessage color="danger">
            <p>
              {permissionsError ||
                'We were unable to connect to your camera or audio device, please make sure you gave permission when asked, and there are no other programs using the camera right now.'}
            </p>
          </BulmaMessage>

          <div className="buttons is-centered mb-6">
            <button className="button is-large is-danger" onClick={() => window.location.reload()}>
              Try Again (Reload Page)
            </button>
          </div>
        </>
      )}

      <div className="columns">
        <div className="column is-10">
          {roomState === 'disconnected' ? (
            <WaitingRoom onEnterCall={onEnterCall} />
          ) : (
            <>
              {videoSupervision && (
                <table className="table is-bordered is-fullwidth">
                  <tr>
                    <th>Order</th>
                    <td>
                      <a href={`/dashboard/order/${videoSupervision.order.id}`} target="_blank">
                        {videoSupervision.order.id}
                      </a>
                    </td>
                    <td>
                      <a href={`mailto:${videoSupervision.order.email}`} target="_blank">
                        {videoSupervision.order.email}
                      </a>
                    </td>
                  </tr>
                </table>
              )}

              {isConnecting && 'Connecting...'}

              {hasMediaDevices && (
                <div>
                  <Room
                    videoSupervision={data.getNextVideoSupervision.videoSupervision}
                    onKitComplete={(kitId) => setCompletedKitIds((prev) => [...prev, kitId])}
                  />
                  {chatSession && supportUserSession && secret && (
                    <ChatAvatar
                      chatSession={chatSession}
                      userSession={supportUserSession}
                      otherUserSession={customerUserSession}
                      secret={secret}
                      header=""
                      initialText="Send Message..."
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="column is-2">
          <Instructions />

          <div className="level-left ml-2">
            {roomState === 'connected' && (
              <CompleteCallButtons
                videoSupervisionId={data.getNextVideoSupervision.videoSupervision.id}
                allKitsCompleted={data.getNextVideoSupervision.videoSupervision.kits.every((kit) =>
                  completedKitIds.includes(kit.id)
                )}
                onExit={() => room.disconnect()}
              />
            )}
          </div>

          <hr />

          {hasMediaDevices && (
            <div className="mt-5">
              <div className="">
                <LocalVideoPreview />
              </div>
            </div>
          )}

          <div className="is-centered">
            <button className="button is-outline" onClick={toggleAudioEnabled}>
              {!hasAudioTrack ? 'No Audio' : isAudioEnabled ? 'Mute' : 'Unmute'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

const VideoPage: PageComponent = () => {
  const [error, setError] = useState()
  const connectionOptions = useConnectionOptions()

  return (
    <div className="container">
      <DashboardTitle title="Video" />

      {error && <p>ERROR: {error}</p>}

      <VideoProvider options={connectionOptions} onError={setError}>
        <InnerPage />
      </VideoProvider>
    </div>
  )
}

VideoPage.displayName = 'Video'
VideoPage.path = '/video'
VideoPage.allowedGroups = ['dashboard', 'video_verifiers']

export default VideoPage
